import { translation } from '../core';
import type { fr } from './fr';

export const en = translation<typeof fr>()({
  'http.network': 'Cannot connect. Check your network, then try again.',
  'http.timeout': 'The server is taking too long to answer. Try again in a moment.',
  'http.unauthorized': 'Sign in to continue.',
  'http.sessionExpired': 'Your session has expired. Please sign in again.',
  'http.forbidden': 'You do not have access to this item.',
  'http.readOnly': 'Read-only mode: when testing locally on real data, this action is disabled (nothing is sent).',
  'http.not_found': 'Item not found.',
  'http.unavailable': 'The service is temporarily unavailable. Try again in a moment.',
  'http.invalid': 'The request could not be processed. Check the information you entered.',
  'http.tooMany': 'Too many requests in a short time. Wait a moment, then try again.',
  'http.badResponse': 'Unexpected answer from the server. Try again in a moment.',

  'auth.INVALID_CREDENTIALS': 'Incorrect email or password.',
  'auth.ACCOUNT_NOT_FOUND': 'No account exists with this email.',
  'auth.GOOGLE_AUTH_ACCOUNT': 'This account uses Google sign-in: continue with Google.',
  'auth.INVALID_GOOGLE_TOKEN': 'Google sign-in failed: try again, or use your email.',
  'auth.ACCOUNT_CONFLICT': 'A Cardnd account already exists with these details: sign in with your email and password.',
  'auth.GOOGLE_SIGNIN_ERROR': 'Google sign-in did not go through. Try again in a moment.',
  'auth.EMAIL_EXISTS': 'An account already exists with this email.',
  'auth.DB_INDEX_CONFLICT': 'Sign-up is temporarily unavailable. Try again later.',
  'auth.INVALID_PHONE_NUMBER': 'Invalid phone number. Expected format: 06 12 34 56 78.',
  'auth.INVALID_CLIENT_NAME': 'The name must be between 2 and 80 characters long.',
  'auth.INVALID_DATE_OF_BIRTH': 'Invalid date of birth.',
  'auth.INVALID_BIO': 'The bio cannot be longer than 500 characters.',
  'auth.IMAGE_TOO_LARGE': 'The image is too large (10 MB maximum).',
  'auth.UNSUPPORTED_IMAGE_TYPE': 'Image format not supported. Use JPG, PNG, WEBP or HEIC.',
  'auth.UPLOAD_FAILED': 'The image upload failed. Please try again.',
  'auth.googleUnavailable': 'Google sign-in is unavailable for now: use your email.',
  'auth.resetNoCode': 'First request a code from “Forgot password”, on this device.',
  'auth.resetBadCode': 'Invalid or expired code: request a new one.',

  'coupon.empty': 'Enter a promo code.',
  'coupon.notFound': 'Invalid or expired promo code.',
  'coupon.limit': 'This promo code has reached its usage limit.',

  'resume.ALREADY_PAID': 'This booking is already paid: you will find it in My trips.',
  'resume.VEHICLE_UNAVAILABLE':
    'This vehicle is no longer available on these dates: choose other dates or another model.',
  'resume.EXPIRED_CART': 'This booking has expired: book again for these dates.',
  'resume.EXPIRED_DATES': 'The dates of this booking have passed: make a new booking.',
  'resume.NOT_RESUMABLE': 'This booking can no longer be paid.',
  'resume.NOT_ONLINE': 'This booking is not paid online.',
  'resume.RATE_UNAVAILABLE':
    'Payment in this currency is temporarily unavailable. Try again in a moment or pay in MAD.',

  vehicleGone: 'This vehicle is no longer available.',
  minDays: {
    one: 'This vehicle is rented for {count} day minimum: extend the rental.',
    other: 'This vehicle is rented for {count} days minimum: extend the rental.',
  },
  returnBeforeStart: 'The return date must be after the pickup date.',
  'booking.invalid': 'The booking could not be created. Check the dates and the driver details.',
  'booking.taken': 'This model is no longer available on these dates: choose other dates.',
  'booking.licence': 'Your driving licence must be at least 2 years old to book.',
  'booking.paymentInit': 'Online payment could not be started. Your request is kept: try again in a moment.',
  bookingNotFound: 'Booking not found.',
  bookingSignIn: 'Sign in to see this booking.',
  tripsSignIn: 'Sign in to see your trips.',

  experienceGone: 'This experience is no longer available.',
  'slot.noSeatsAt': {
    one: 'Not enough seats left at {time} for {count} person: choose another departure.',
    other: 'Not enough seats left at {time} for {count} people: choose another departure.',
  },
  'slot.noSeatsDay': {
    one: 'Not enough seats left that day for {count} person: choose another departure.',
    other: 'Not enough seats left that day for {count} people: choose another departure.',
  },
  'slot.noDepartureAt': 'No more departures at {time} that day: choose another time.',
  'slot.noDeparture': 'No more departures that day: choose another date.',
  'experienceBooking.invalid': 'The booking could not be created. Check the participants and the requested details.',
  'experienceBooking.slotGone': 'This departure is no longer available: choose another date.',
  'experienceBooking.slotTaken': 'This departure is no longer available: choose another time or another date.',
  'experienceBooking.details': 'Some details the provider asks for are missing or not valid.',
  'experienceBooking.waiver': 'Accept the provider’s waiver to book.',
  'experienceBooking.account': 'You need an account to book an experience.',
  'eligibility.signIn': 'Sign in to request free entry.',
  'eligibility.tooMany': 'You already have too many pending requests: wait for the provider’s answer.',
  'eligibility.notFound': 'Free-entry request not found.',
  'tickets.notIssued': 'The provider’s tickets have not been issued yet.',
  'tickets.wait': 'Wait a minute before sending them again.',
  'tickets.failed': 'Cannot send for now, try again later.',

  cancelClosed: 'This booking can no longer be cancelled.',
  'cancel.experienceTerms': 'Experiences follow the provider’s cancellation terms.',
  'extension.unavailable': 'The vehicle is not available for the new dates requested.',
  'rating.comment': 'Add a comment to your review.',
  'rating.alreadyRated': 'You have already rated this experience.',
  'rating.notYet': 'You have already rated this experience, or it has not taken place yet.',
  'voucher.signIn': 'Sign in to download the voucher.',
  'voucher.notReady': 'The booking voucher is not available yet.',
  'voucher.failed': 'The booking voucher could not be downloaded.',

  'favourites.signIn': 'Sign in to see your favourites.',
  'favourites.save': 'Sign in to save your favourites.',
  itemGone: 'This item is no longer available.',
  'messages.signIn': 'Sign in to see your messages.',
  'messages.closed': 'This conversation has ended: you can read it, but no longer reply.',
  'messages.rated': 'This conversation has already been rated.',
  'messages.botChoice': 'Choose one of the suggested answers to continue.',
  'reviews.signIn': 'Sign in to see your reviews.',
  'reviews.load': 'Your reviews could not be loaded.',
  'reviews.notYours': 'This review is not yours.',
  'reviews.gone': 'This review no longer exists.',

  agencyNotFound: 'This agency cannot be found.',
  articleNotFound: 'This article cannot be found.',
  'partner.experiences': 'Partnership requests for experiences are not online yet: write to us from the Contact page.',
  'partner.signIn': 'Sign in to send your request.',
  'partner.invalid': 'Check the phone numbers and the required fields.',
  'partner.exists': 'A request already exists for this email.',
  'partner.failed': 'The request could not be saved. Try again in a moment.',
  'newsletter.email': 'This email address is not accepted: please check it.',
  'contact.missing': 'Fill in the name, email, subject and message, then try again.',
  'contact.failed': 'The message could not be sent. Try again in a moment.',
  'contact.network': 'The message could not be sent: check your connection, then try again.',

  'payments.unavailable': 'Online payment is temporarily unavailable. Try again in a moment.',
  'payments.finalizing': 'Your card is pre-authorised; Cardnd needs a little more time to register it.',
  'payments.signIn': 'Sign in to pay for this booking.',
  'payments.init': 'Online payment could not be started. Your booking is kept: try again in a moment.',
});
